import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import {
  ROLE_TEXT,
  roleColor,
  type RosterCategory,
  type RosterEmployee,
  useEmployeesT,
  useHireHref,
} from './kit';

// Phones show the first cards and a button for the rest.
const PHONE_PREVIEW = 6;

/** The hire gallery: every ready-made role, filterable by kind of work. */
export default function EmployeesRoster({
  roster,
  categories,
}: {
  roster: RosterEmployee[];
  categories: RosterCategory[];
}) {
  const { t } = useEmployeesT();
  const [category, setCategory] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const visible = roster.filter(
    (employee) => category === 'all' || employee.category === category,
  );
  const labelFor = (id: string) =>
    t(categories.find((item) => item.id === id)?.label ?? id);
  const filters = [{ id: 'all', label: 'All roles' }, ...categories];

  return (
    <section id='roles' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
          <SectionHeading
            title={t('{{count}} roles, ready to hire.', {
              count: roster.length,
            })}
            tail={t('Or describe your own.')}
            lead={t(
              'Each role comes ready: a name, a portrait, instructions written for the job and ideas for what to ask first. Rename them, change anything, and hire in a click.',
            )}
          />
        </div>

        <div
          role='group'
          aria-label={t('Filter roles')}
          className='-mx-6 mt-10 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden'
        >
          {filters.map((filter) => {
            const active = category === filter.id;
            return (
              <button
                key={filter.id}
                type='button'
                aria-pressed={active}
                onClick={() => setCategory(filter.id)}
                className={clsxm(
                  'flex-none rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                  active
                    ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                    : clsxm(
                        'bg-white hover:ring-[#0f0f28]/25 dark:bg-white/[0.05] dark:hover:ring-white/30',
                        tone.line,
                        tone.body,
                      ),
                )}
              >
                {t(filter.label)}
              </button>
            );
          })}
        </div>

        <ul className='mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5'>
          {visible.map((employee, index) => (
            <RoleCard
              key={employee.slug}
              employee={employee}
              categoryLabel={labelFor(employee.category)}
              className={
                !expanded && index >= PHONE_PREVIEW
                  ? 'hidden sm:block'
                  : undefined
              }
            />
          ))}
          <CustomRoleCard
            className={
              !expanded && visible.length >= PHONE_PREVIEW
                ? 'hidden sm:block'
                : undefined
            }
          />
        </ul>

        {!expanded && visible.length > PHONE_PREVIEW ? (
          <button
            type='button'
            onClick={() => setExpanded(true)}
            className={clsxm(
              'mt-6 w-full rounded-full bg-white py-3 text-[15px] font-semibold sm:hidden dark:bg-white/[0.06]',
              tone.line,
              tone.ink,
            )}
          >
            {t('Show all {{count}} roles', { count: visible.length })}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function RoleCard({
  employee,
  categoryLabel,
  className,
}: {
  employee: RosterEmployee;
  categoryLabel: string;
  className?: string;
}) {
  const { t } = useEmployeesT();
  const href = useHireHref(employee.slug);

  return (
    <li className={className}>
      <Link
        href={href}
        className={clsxm(
          'group flex h-full flex-col rounded-[22px] bg-white p-1.5 transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-28px_rgba(22,12,90,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-[#14141f]',
          tone.line,
        )}
      >
        <span
          className='relative block aspect-square overflow-hidden rounded-[17px]'
          style={{ backgroundColor: `${employee.color}26` }}
        >
          <Image
            src={employee.photoLarge}
            alt={t('{{name}}, {{role}}', {
              name: employee.name,
              role: t(employee.role),
            })}
            fill
            unoptimized
            sizes='(min-width: 1280px) 230px, (min-width: 640px) 30vw, 46vw'
            className='object-cover transition duration-500 group-hover:scale-[1.03]'
          />
          <span className='absolute start-2.5 top-2.5 hidden rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-[#10101c] backdrop-blur-sm sm:block'>
            {categoryLabel}
          </span>
        </span>
        <span className='flex flex-1 flex-col px-2.5 pb-2.5 pt-3.5 sm:px-3'>
          <span
            className={clsxm(
              'text-[17px] font-semibold leading-tight tracking-[-0.01em]',
              tone.ink,
            )}
          >
            {employee.name}
          </span>
          <span
            className={clsxm(
              'mt-0.5 text-[13px] font-semibold leading-snug',
              ROLE_TEXT,
            )}
            style={roleColor(employee.color)}
          >
            {t(employee.role)}
          </span>
          <span
            className={clsxm(
              'mt-2 line-clamp-3 text-[13px] leading-[1.45] sm:text-sm',
              tone.body,
            )}
          >
            {t(employee.tagline)}
          </span>
          <span
            className={clsxm(
              'mt-auto flex items-center gap-1.5 pt-3.5 text-[13px] font-semibold transition-colors group-hover:text-primary-700 dark:group-hover:text-primary-300',
              tone.ink,
            )}
          >
            {t('Hire {{name}}', { name: employee.name })}
            <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
          </span>
        </span>
      </Link>
    </li>
  );
}

function CustomRoleCard({ className }: { className?: string }) {
  const { t } = useEmployeesT();
  // Hiring from scratch: the hire screen without a template.
  const href = useHireHref('scratch');

  return (
    <li className={className}>
      <Link
        href={href}
        className='group flex h-full min-h-[18rem] flex-col justify-between rounded-[22px] border border-dashed border-[#0f0f28]/20 bg-[#f3f3f8] p-5 transition duration-300 hover:border-primary-400 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-white/15 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]'
      >
        <span className='flex h-11 w-11 items-center justify-center rounded-full bg-[#10101c] text-xl text-white dark:bg-white dark:text-[#10101c]'>
          +
        </span>
        <span>
          <span
            className={clsxm(
              'block text-[17px] font-semibold leading-tight',
              tone.ink,
            )}
          >
            {t('Describe your own')}
          </span>
          <span
            className={clsxm('mt-2 block text-sm leading-[1.45]', tone.body)}
          >
            {t(
              'Write what the job is in a sentence. We draft the name, portrait and instructions for you to check.',
            )}
          </span>
          <span
            className={clsxm(
              'mt-4 flex items-center gap-1.5 text-[13px] font-semibold group-hover:text-primary-700 dark:group-hover:text-primary-300',
              tone.ink,
            )}
          >
            {t('Create a role')}
            <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
          </span>
        </span>
      </Link>
    </li>
  );
}
